import * as React from 'react'
import { SingleQuestion } from './index'
import { useAngularServices } from '@/react/components'
import { useRouter } from '@/react/hooks'
import {
  ApplicationConfig,
  GenericOption,
  ReportAnswer,
  ReportQuestion,
} from '@/react/types'
import { Dispatch, SetStateAction } from 'react'

type SingleIdQuestionProps = {
  questionInfo: ReportQuestion
  ANSWER: ReportAnswer
  setPartyObservedOptions: Dispatch<SetStateAction<GenericOption[]>>
  partyObservedOptions: GenericOption[]
  globalPartyObserved: GenericOption[]
  app: ApplicationConfig
  saveButtonPressed: number
  memoryState: {
    [key: string]: ReportAnswer[]
  }
  setMemoryState: Dispatch<
    SetStateAction<{ [key: string]: ReportAnswer[] } | null>
  >
  index: number
  answerTitleSet: () => void
  parentCategoryId: number
}

export function SingleIdQuestionsElement({
  questionInfo,
  memoryState,
  setMemoryState,
  ANSWER,
  globalPartyObserved,
  partyObservedOptions,
  setPartyObservedOptions,
  app,
  saveButtonPressed,
  answerTitleSet,
  parentCategoryId,
}: SingleIdQuestionProps) {
  const questionElementId = `${questionInfo.id}-${parentCategoryId}`
  const answersLink = memoryState[questionElementId]

  const [answersToRender, setAnswersToRender] = React.useState(answersLink)
  const { Api } = useAngularServices()
  const { stateService } = useRouter()
  const observationID = stateService.params.observation

  // Keep answersToRender synchronized with memoryState
  React.useEffect(() => {
    const currentAnswersLink = memoryState[questionElementId]
    if (currentAnswersLink) {
      setAnswersToRender(currentAnswersLink)
    }
  }, [memoryState, questionElementId])

  if (!answersToRender) return null

  return (
    <div>
      {answersToRender.map((singleAnswer, idx) => {
        // Use array index (idx) instead of copy_sequence_number for array access
        const currentAnswersLink = memoryState[questionElementId]
        if (!currentAnswersLink || !currentAnswersLink[idx]) {
          console.error('Missing answer at index:', {
            idx,
            currentAnswersLink,
            singleAnswer,
            questionInfo,
          })
          return null
        } else {
          return (
            <SingleQuestion
              globalPartyObserved={globalPartyObserved}
              partyObservedOptions={partyObservedOptions}
              setPartyObservedOptions={setPartyObservedOptions}
              key={`${questionInfo.id}-${singleAnswer.id}-${idx}`}
              questionInfo={questionInfo}
              memoryState={memoryState}
              setMemoryState={setMemoryState}
              copyField={copyField}
              app={app}
              saveButtonPressed={saveButtonPressed}
              index={idx}
              answerTitleSet={answerTitleSet}
              answersLink={currentAnswersLink[idx]}
              parentCategoryId={parentCategoryId}
            />
          )
        }
      })}
    </div>
  )

  async function copyField() {
    try {
      const { data: newAnswerResponse } = await Api.post<ReportAnswer>(
        `answers`,
        {
          observation: parseInt(observationID as string),
          category: parentCategoryId,
          question: questionInfo.id,
        },
      )

      // The API returns a single ReportAnswer object, not an array
      if (!newAnswerResponse || !newAnswerResponse.id) {
        console.error(
          'Invalid response from API when creating new answer:',
          newAnswerResponse,
        )
        return
      }

      const newAnswerData = newAnswerResponse

      if (!newAnswerData?.id) {
        console.error('New answer data missing required id:', newAnswerData)
        return
      }

      // Create a fresh answer object with all required properties
      const newCopiedAnswer: ReportAnswer = {
        // Use data from API response where available
        id: newAnswerData.id,
        copy_sequence_number:
          newAnswerData.copy_sequence_number || answersLink.length,
        category: Number(newAnswerData.category) || parentCategoryId,
        question: Number(newAnswerData.question) || questionInfo.id,
        observation:
          Number(newAnswerData.observation) ||
          parseInt(observationID as string),
        answer: newAnswerData.answer || 'n/a',
        severity: newAnswerData.severity || 1,
        reference: newAnswerData.reference || '',
        notes: newAnswerData.notes || [],
        corrective_actions: newAnswerData.corrective_actions || [],
        photos: newAnswerData.photos || [],
        party_observed: newAnswerData.party_observed || [],
        answer_party_observed_answer_list:
          newAnswerData.answer_party_observed_answer_list || [],
        party_observed_easy_acces_data:
          newAnswerData.party_observed_easy_acces_data || {},
        edited: true,
        deleted: newAnswerData.deleted || false,
        // Required properties from ReportAnswer interface
        client_object_key: newAnswerData.client_object_key || '',
        date_created: newAnswerData.date_created || new Date().toISOString(),
        date_updated: newAnswerData.date_updated || new Date().toISOString(),
        internal_assignees: newAnswerData.internal_assignees || [],
        lat: newAnswerData.lat || null,
        lon: newAnswerData.lon || null,
        location_radius: newAnswerData.location_radius || null,
        location_age: newAnswerData.location_age || null,
        synchronised: newAnswerData.synchronised || [],
        user: newAnswerData.user || 0,
      }

      // Update both the local answersLink and set new state
      const updatedAnswersLink = [...answersLink, newCopiedAnswer]
      setAnswersToRender(updatedAnswersLink)

      // Update the global memoryState to ensure the copied answer is properly tracked
      setMemoryState((prevState) => {
        if (!prevState) return prevState
        const newState = { ...prevState }
        newState[questionElementId] = updatedAnswersLink
        return newState
      })
    } catch (error) {
      console.error('Error copying field:', error)
    }
  }
}
